import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantDocumentTypesApi } from "../../api/tenantDocumentTypesApi.js";
import { toast } from "../Toast.js";
import { Button } from "../ui/Button.js";

const inputStyle: React.CSSProperties = {
  height: 32,
  border: "1px solid #cbd5e1",
  borderRadius: 8,
  padding: "0 10px",
  fontSize: 13,
  fontFamily: "inherit",
  background: "#fff",
  boxSizing: "border-box",
  flex: 1,
};

/**
 * 資格（組み込み・カスタム問わず）1件あたりの「この資格の請求書」セクション。
 * 設計書: docs/design/document-type-master-phase2.md §8.2
 *
 * カスタム資格（例: 測量士）が自分専用の請求書（例: 測量士請求書）を持てるようにする。
 * kind='invoice' のみ作成可能（見積書・計算書の新規作成はここからは行わない）。
 */
export function ProfessionDocumentTypesSection({
  profession,
  qualificationLabel,
}: {
  profession: string;
  qualificationLabel: string;
}) {
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [label, setLabel] = useState(`${qualificationLabel}請求書`);
  const [withholdingEligible, setWithholdingEligible] = useState(true);

  const docTypesQuery = useQuery({
    queryKey: ["tenant-document-types"],
    queryFn: () => tenantDocumentTypesApi.list(),
  });

  const createMut = useMutation({
    mutationFn: () => tenantDocumentTypesApi.create({
      label: label.trim(),
      profession,
      kind: "invoice",
      withholding_eligible: withholdingEligible,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-document-types"] });
      setFormOpen(false);
      toast("請求書の書類種別を作成しました");
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  const deleteMut = useMutation({
    mutationFn: (docType: string) => tenantDocumentTypesApi.remove(docType),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant-document-types"] });
      toast("書類種別を削除しました");
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  const docTypes = (docTypesQuery.data?.data ?? []).filter(
    (d) => d.profession === profession && d.kind === "invoice",
  );

  return (
    <div style={{ padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
      <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800, marginBottom: 8 }}>
        この資格の請求書
      </div>

      {docTypesQuery.isLoading ? (
        <div style={{ fontSize: 12, color: "#94a3b8" }}>読み込み中...</div>
      ) : (
        <div style={{ display: "grid", gap: 6, marginBottom: 10 }}>
          {docTypes.length === 0 && (
            <div style={{ fontSize: 12, color: "#94a3b8" }}>まだ書類種別がありません。</div>
          )}
          {docTypes.map((d) => (
            <div key={d.doc_type} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
              padding: "6px 10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{d.label}</span>
                {d.is_builtin ? (
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#64748b", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 99, padding: "1px 7px" }}>
                    組み込み
                  </span>
                ) : (
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#0f766e", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 99, padding: "1px 7px" }}>
                    カスタム
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                <Link
                  to={`/settings/templates/new?type=${d.doc_type}`}
                  style={{ fontSize: 12, fontWeight: 700, color: "#0f766e", textDecoration: "none" }}
                >
                  テンプレートを作成 →
                </Link>
                {!d.is_builtin && (
                  <Button
                    variant="ghost"
                    style={{ padding: "2px 8px", color: "#dc2626" }}
                    disabled={deleteMut.isPending}
                    onClick={() => { if (confirm(`「${d.label}」を削除しますか？`)) deleteMut.mutate(d.doc_type); }}
                  >
                    削除
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!formOpen ? (
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          style={{ background: "none", border: "none", padding: 0, fontSize: 12, color: "#0f766e", fontWeight: 700, cursor: "pointer" }}
        >
          + {qualificationLabel}請求書を作成
        </button>
      ) : (
        <div style={{ padding: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, display: "grid", gap: 8 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 700, width: 60, flexShrink: 0 }}>ラベル</span>
            <input style={inputStyle} value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 700, width: 60, flexShrink: 0 }}>源泉徴収</span>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
              <input type="radio" checked={withholdingEligible} onChange={() => setWithholdingEligible(true)} />
              あり
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
              <input type="radio" checked={!withholdingEligible} onChange={() => setWithholdingEligible(false)} />
              なし
            </label>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="outline"
              disabled={!label.trim() || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              作成
            </Button>
            <Button variant="ghost" onClick={() => setFormOpen(false)}>キャンセル</Button>
          </div>
        </div>
      )}
    </div>
  );
}
